import React, { useContext, useState, useEffect } from "react";
import { graphql, useStaticQuery } from "gatsby";
import { useLocation } from "@reach/router";
import {
  BlockContent,
  Seo,
  StandardPaddingWrapper,
} from "@components";
import { LanguageContext } from "@contexts";
import { HappeningThumbnail } from "@features/article";
import * as styles from "./pageStyles/whatsnew.module.scss";
import { RegularHeaderBlock } from "src/common/components/BlockContent/RegularHeader/RegularHeaderBlock";
import { Button } from "@ui/buttons";

const WhatsNew = () => {
  const {
    allSanityNews,
    allSanityEvent,
    allSanityNewsTag,
    sanityWhatsNew: { text, pdf },
  } = useStaticQuery(WHATS_NEW_QUERY);

  // Prepare upcoming events: filter for future events and sort ascending by startTime
  const now = new Date();
  const upcomingSortedEvents = allSanityEvent.edges
    .filter(({ node }: { node: { startTime?: string } }) => {
      return node.startTime && new Date(node.startTime) > now;
    })
    .sort((a: { node: { startTime?: string } }, b: { node: { startTime?: string } }) => {
      const timeA = a.node.startTime ? new Date(a.node.startTime).getTime() : Infinity;
      const timeB = b.node.startTime ? new Date(b.node.startTime).getTime() : Infinity;
      if (timeA === Infinity && timeB === Infinity) return 0;
      return timeA - timeB; // Ascending
    });

  // Prepare news items: sort descending by publishDate
  const sortedNewsItems = [...allSanityNews.edges].sort(
    (a: { node: { publishDate?: string } }, b: { node: { publishDate?: string } }) => {
      const dateA = a.node.publishDate ? new Date(a.node.publishDate).getTime() : -Infinity;
      const dateB = b.node.publishDate ? new Date(b.node.publishDate).getTime() : -Infinity;
      if (dateA === -Infinity && dateB === -Infinity) return 0;
      return dateB - dateA; // Descending
    }
  );

  // Combine upcoming events and sorted news: events first, then news
  const initialNews = [...upcomingSortedEvents, ...sortedNewsItems];

  // Read the URL's filter param on load:
  const location = useLocation();
  const [sorting, setSorting] = useState("all");
  const [news, setNews] = useState(initialNews);
  const [initialized, setInitialized] = useState(false);

  const { localize } = useContext(LanguageContext);

  // Prepare a list of tags with their post counts:
  const newsTag = allSanityNewsTag.edges.map(({ node: tag }) => {
    const sortingValue = (tag.name?.nb) || tag.name?.en || "";
    const postCount = initialNews.filter(({ node }) =>
      node.newsTag?.name &&
      (
        node.newsTag.name.nb === sortingValue ||
        node.newsTag.name.en === sortingValue
      )
    ).length;

    return {
      ...tag,
      sorting: sortingValue, // used for filtering
      postCount,
    };
  });

  // Build all categories, each has a "sorting" value and localized name
  const categories = [
    {
      name: { nb: "Alle", en: "All" },
      sorting: "all",
      postCount: initialNews.length,
    },
    {
      name: { nb: "Arrangementer", en: "Events" },
      sorting: "event",
      postCount: initialNews.filter(({ node }) => node._type === "event").length,
    },
    ...newsTag,
  ];

  // On initial load, if there's a ?filter= param, apply that filter:
  useEffect(() => {
    if (!initialized) {
      const params = new URLSearchParams(location.search);
      const filterParam = params.get("filter");
      if (filterParam && filterParam !== sorting) {
        applyFilter(filterParam);
      }
      setInitialized(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialized, location.search, sorting]);

  // Applies the chosen filter to set sorting + the "news" array
  const applyFilter = (name: string) => {
    if (name === "all") {
      setSorting("all");
      setNews(initialNews);
    } else {
      const filtered = initialNews.filter(({ node }) =>
        (node._type === name) ||
        (
          node.newsTag?.name &&
          (
            node.newsTag.name.nb === name ||
            node.newsTag.name.en === name
          )
        )
      );
      setSorting(name);
      setNews(filtered);
    }
  };

  // Changes the filter by pushing a new state into history
  // instead of a full navigation—this prevents scroll reset
  const changeSorting = (name: string) => {
    if (name === "all" || name === sorting) {
      window.history.pushState({}, "", location.pathname);
      applyFilter("all");
    } else {
      window.history.pushState({}, "", `${location.pathname}?filter=${name}`);
      applyFilter(name);
    }
  };

  const firstItemWithImage = initialNews.length > 0 && initialNews[0].node.content?.mainImage
    ? initialNews[0].node.content.mainImage
    : undefined;

  return (
    <>
      <RegularHeaderBlock
        firstLine={{ en: "What's Up", nb: "Hva skjer" }}
        secondLine={{ en: "Bukta", nb: "Bukta" }}
        text={text}
        pdf={pdf}
      />

      <StandardPaddingWrapper className={styles.eventPage}>
        <Seo
          title={localize({ nb: "Hva skjer?", en: "Whats new?" })}
          ogImage={firstItemWithImage}
        />

        {/* Filter Buttons */}
        <div className={styles.categoryFilter}>
          {categories
            .filter((cat) => cat.postCount >= 5) // for example, hide if < 5 posts
            .map((cat, index) => {
              // If sorting state matches cat.sorting,
              // apply the activeCategoryButton style:
              const isActive = sorting === cat.sorting;
              return (
                <Button
                  key={index}
                  onClick={() => changeSorting(cat.sorting)}
                  className={isActive ? styles.activeCategoryButton : ""}
                >
                  {localize(cat.name)}
                </Button>
              );
            })}
        </div>

        <div className={styles.grid}>
          {news.map(({ node: item }, index) =>
            item.content &&
            item.slug &&
            item.content.mainImage &&
            item.content.body ? (
              <HappeningThumbnail item={item} key={index} />
            ) : null
          )}
        </div>
      </StandardPaddingWrapper>
    </>
  );
};

export const WHATS_NEW_QUERY = graphql`
  query {
    allSanityNews {
      edges {
        node {
          _type
          notOnFrontPage
          content {
            title: _rawTitle
            introduction: _rawIntroduction
            body: _rawBody
            mainImage {
              asset {
                gatsbyImageData(fit: FILLMAX, width: 800)
              }
              hotspot {
                y
                x
              }
            }
          }
          slug {
            current
          }
          publishDate
          newsTag {
            name: _rawName
          }
        }
      }
    }
    allSanityEvent {
      edges {
        node {
          _type
          content {
            title: _rawTitle
            introduction: _rawIntroduction
            body: _rawBody
            mainImage {
              asset {
                gatsbyImageData(fit: FILLMAX, width: 800)
              }
              hotspot {
                x
                y
              }
            }
          }
          slug {
            current
          }
          startTime
          endTime
        }
      }
    }
    allSanityNewsTag {
      edges {
        node {
          name: _rawName
        }
      }
    }
    sanityWhatsNew(_id: { regex: "/whatsNew$/" }) {
      text: _rawText
      pdf {
        asset {
          url
        }
      }
    }
  }
`;

export default WhatsNew;
